// SpeechSynthesizer.js
import React, { useState, useRef, useEffect } from 'react';
import { Box, 
    Button, 
    TextField, 
    Typography, 
    IconButton, 
    Paper, 
    CircularProgress, 
    Tooltip} from '@mui/material';
import { PlayArrow, 
         Loop, 
         Delete, 
         ArrowBack, 
         Timer, 
         Pause, 
         Stop } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import TranslateIcon from '@mui/icons-material/Translate';
import {serverUrl, configKeys} from './ConfigHolder';
import { useConfig } from './ConfigHolder';
import PlaybackSlider from './components/PlaybackSlider';
import { get_auth, post_auth } from './auth';
import FormInfo from './FormInfo';
import { FormTranslate} from './FormTranslate';
import { useAuth } from './AuthProvider';

import { useAutoPlay } from './contexts/AutoPlayContext';
import { BasicEvents, BasicStates } from './fsm/AutoPlayBaseFSM';
import { isRTL } from './generalUtils';
import { Sentence, MyConversationSentence, ConversationSentence} from './models/Sentence'


import ErrorDispatcher from './ErrorDispatcher';

import VoiceSelector from './components/VoiceSelector';
import AddToMyContentDialog from './components/AddToMyContentDialog';

// SpeechSynthesizer is responsible for:
//   - Managing the practice text textarea (what the learner wants to say).
//   - Requesting TTS audio for that text and reporting the audio duration
//     back to the parent (via aimtimeUpdateCallBack) so VoiceRecorder can
//     compare user speech duration to the TTS reference.
//   - Exposing the text upstream via inputTextCallBack. In VoiceRecorder this
//     is stored as practiceText and any change clears the current **session
//     attempts** while leaving global MongoDB fields untouched.

const synthesizeAudio = async (inputTextValue, lang_code, selectedGender, lang, serverUrl, store_voice = {}, logout) => {
    const data = {
        input_text: inputTextValue,
        lang_code: lang_code,
        gender: selectedGender,
        language: lang,
        encoding: 'mp3',
        store_voice,
    };

    let uri = serverUrl + '/synthesize';
    try {
        const response = await post_auth(uri, data, logout);
        return response.data;  // Return the necessary data
    } catch (error) {
        console.error('Synthesis failed', error);
        throw error;
    }
};

const SpeechSynthesizer = (props) => {
    // practiceTextValue mirrors props.inputText and represents the current
    // practice text shown in the textarea. It is **local UI state** only;
    // VoiceRecorder owns the canonical practiceText and its session history.
    const [practiceTextValue, setPracticeTextValue] = useState("");
    const [predefSentence, setPredefSentence] = useState(null);
    const [audioData, setAudioData] = useState({});
    const [lastInputModified, setLastInputModified] = useState(true);
    const { config, updateConfig } = useConfig();
    const genderKey = [configKeys.genderKey];
    const [playbackRate, setPlaybackRate] = useState(1.0);
    const [selectedGender, setSelectedGender] = useState(config[genderKey]);
    const [isRepeatPlaying, setIsRepeatPlaying] = useState(false);
    const textareaRef = useRef(null);
    // const [selectionRange, setSelectionRange] = useState({ start: 0, end: 0 }); // ramainings of the attempt to play selected text
    const lang_code = config[configKeys.langLearn].value;
    const lang = config[configKeys.langLearn].label;
    const explanations = useRef([]);
    const theme = useTheme();
    const {logout } = useAuth();
    const [error, setError] = useState(null);
    const [addInProgress, setAddInProgress] = useState(false); // State to control add button
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [showPlaybackControls, setShowPlaybackControls] = useState(false);
    const [pausedAt, setPausedAt] = useState(0);
    const audioRef = useRef(null);
    const isPausedRef = useRef(false);
    const [repeatTotal, setRepeatTotal] = useState(1);      // total times to play (default 1)
    const [repeatPlayed, setRepeatPlayed] = useState(0);    // how many times already played

    // const navigate = useNavigate(); 

    const [isFormTranslateOpen, setIsFormTranslateOpen] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false); // State for popup visibility
    const ctx = useAutoPlay();
    const CHARLIMIT = 400;
    const [myContentInsertMode, setMyContentInsertMode] = useState('above');

    const isAutoPlayActive = (ctx.autoPlayState !== BasicStates.IDLE);
    const [disabledGender, setDisabledGender] = useState(''); // values = {MALE,FEMALE}

    const openFormTranslate = () => {
        setIsFormTranslateOpen(true);
        };
    
    const closeFormTranslate = () => {
        setIsFormTranslateOpen(false);
        };
    
    const resetState = () => {
        setLastInputModified(true);
        setAudioData(null);
        setPredefSentence(null);
        explanations.current = [];
    }

    const limitInput = (s) => {
         // Enforce a  character limit and update state/callback with truncated value
        let val = s || '';
        // val = val.trim();
        if (val.length > CHARLIMIT) {
            val = val.slice(0, CHARLIMIT);
        }
        return val;
    }
    const handleInputChange = (e) => {
       
    let val = limitInput(e.target.value)
    setPracticeTextValue(val);
        if (props.inputTextCallBack) props.inputTextCallBack(val);
        resetState();
    }

    useEffect(() => {
    let val = limitInput(props.inputText)
    setPracticeTextValue(val);
        resetState();
    }, [props.inputText]);

    useEffect(() => {
    setPracticeTextValue('');
        resetState()
      }, [lang_code]); // cleaning up when lang code changes; 2024-03-21 and more 2025-02-03
    
    useEffect(() => {
        if (props.inputSentence && props.inputSentence.getSentence()) {
            if (predefSentence && props.inputSentence.getSentence() === predefSentence.getSentence() &&
                audioData && practiceTextValue === predefSentence.getSentence()) {
                if (props.inputSentence.play) {
                    const playbackTiming = getPlaybackTiming(props.inputSentence);
                    // playAudio(audioData.audio, playbackRate);
                    playAudio1({
                        audioData: audioData.audio,
                        duration: audioData.duration,
                        playbackRate: getEffectivePlaybackRate(
                            playbackRate,
                            playbackTiming,
                            isAutoPlayActive
                        ),
                        times: 1,
                        resumeFrom: 0,
                        resume: false,
                        played: 0,
                        playbackTiming,
                    });
                }
            } else {
                setPredefSentence(props.inputSentence);
                if (props.inputSentence.play) {
                    playPredefined(props.inputSentence, selectedGender);
                }
            }
        }
        // Conversation-specific behavior: lock gender only when the source is a conversation
        if (props.inputSentence?.isConvo && props.source === 'conversation') {
            if (props.inputSentence.gender === 'MALE') {
                setDisabledGender('FEMALE');
            } else {
                setDisabledGender('MALE');
            }
        } else {
            setDisabledGender("");
        };
        
        setAddInProgress(false);
        // eslint-disable-next-line
    }, [props.inputSentence, props.source]);

    useEffect(() => {
        if (ctx?.ui?.stopplay) {
            handleStop();
        }

    }, [ctx?.ui?.stopplay])

    useEffect(() => {
        if (audioData && audioData.audio && !isPlaying && !isPaused) {
            // GUARD: Only play if the currently loaded audio explicitly matches the current sentence!
            if (props.inputSentence.play && audioData.text === props.inputSentence.getSentence()) {
                const playbackTiming = audioData.playbackTiming || getPlaybackTiming(props.inputSentence);
                playAudio1({
                    audioData: audioData.audio,
                    duration: audioData.duration,
                    playbackRate: getEffectivePlaybackRate(
                        playbackRate,
                        playbackTiming,
                        isAutoPlayActive
                    ),
                    times: 1,
                    resumeFrom: 0,
                    resume: false,
                    played: 0,
                    playbackTiming,
                });
            }
        }
    } ,[props.triggerPlay])

    const playOnRateChange = (newRate) => {
        if (audioData && audioData.audio && !isPlaying && !isPaused) {
            const playbackTiming = audioData.playbackTiming || getPlaybackTiming(predefSentence);
            playAudio1({
                audioData: audioData.audio,
                duration: audioData.duration,
                playbackRate: getEffectivePlaybackRate(
                    newRate,
                    playbackTiming,
                    isAutoPlayActive
                ),
                times: 1,
                resumeFrom: 0,
                resume: false,
                played: 0,
                playbackTiming,
            });
        }
    }
 
    const handleClickSynthesize = async (genderVoice) => {
        setIsRepeatPlaying(false); // Hide playback controls for Listen
        if (lastInputModified || genderVoice !== selectedGender) {
            
            // FAST-FAIL: Wipe stale data before synthesizing
            handleStop();
            setAudioData({});

            if (predefSentence && predefSentence.getSentence() === practiceTextValue && predefSentence.sentence) {
                playPredefined(predefSentence, genderVoice);
            } else {
                try {
                    const synthesizedData = await synthesizeAudio(practiceTextValue, lang_code, genderVoice, lang, 
                        serverUrl, {}, logout);
                    // Pass the text to pin the audio to this specific sentence
                    responseProcessing(synthesizedData.audio, synthesizedData.duration, practiceTextValue);
                } catch (error) {
                    console.error('Error during synthesis:', error);
                    if (props.errorCallBack) { props.errorCallBack(error); } else {
                        setError(error);
                    }
                }
            }
        } else {
            const playbackTiming = audioData.playbackTiming || getPlaybackTiming(predefSentence);
            playAudio1({
                audioData: audioData.audio,
                duration: audioData.duration,
                playbackRate: getEffectivePlaybackRate(
                    playbackRate,
                    playbackTiming,
                    isAutoPlayActive
                ),
                times: 1,
                resumeFrom: 0,
                resume: false,
                played: 0,
                playbackTiming,
            });
        }
    };


    // Add a ref to track the previous Blob URL for revocation
const previousBlobUrlRef = useRef(null);

// Add 'associatedText' parameter
const responseProcessing = async (audio64base, duration, associatedText, playbackTiming = null) => {

    // Convert base64 to Blob
    const audioBlob = new Blob(
        [Uint8Array.from(atob(audio64base), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
    );
    const audioUrl = URL.createObjectURL(audioBlob);
    
    // Revoke previous Blob URL to free memory and prevent buffer carryover
    if (previousBlobUrlRef.current) {
        URL.revokeObjectURL(previousBlobUrlRef.current);
    }
    previousBlobUrlRef.current = audioUrl;
    
    // Tag the audioData with the text it belongs to
    setAudioData({ audio: audioUrl, duration, blob: audioBlob, text: associatedText, playbackTiming });
    props.aimtimeUpdateCallBack(duration);
    setLastInputModified(false);
    playAudio1({
        audioData: audioUrl,
        duration,
        playbackRate: getEffectivePlaybackRate(
            playbackRate,
            playbackTiming,
            isAutoPlayActive
        ),
        times: 1,
        resumeFrom: 0,
        resume: false,
        played: 0,
        playbackTiming,
    });
};

    const playPredefined = (predefSentence, voiceGender) => {
        // FAST-FAIL: Immediately stop playing and wipe stale audio data 
        // before we even start pulling the new file
        handleStop(); 
        setAudioData({});

        let voiceObject = null;

        // When invoked from Conversations, use the unified 1-voice interface
        if (props.source === 'conversation' &&
            predefSentence && typeof predefSentence.getConversationVoice === 'function') {
            voiceObject = predefSentence.getConversationVoice();
        } else if (predefSentence?.phraseVal?.voices) {
            // Generic 2-voice interface (ShowContent and other views)
            voiceObject = predefSentence.phraseVal.voices[voiceGender.toUpperCase()] || null;
        }

        if (!voiceObject || !voiceObject.path) {
            console.error('No suitable voice object found for predefined sentence.');
            return;
        }

        const path = voiceObject.path;
        const duration = voiceObject.duration;
        const uri = serverUrl + '/predefined_voice_file';
        const params = { path };

        get_auth(uri, params, 'json')
            // Pass the sentence text down so responseProcessing can tag it
            .then((response) => responseProcessing(
                response.data.audio,
                duration,
                predefSentence.getSentence(),
                getPlaybackTiming(predefSentence)
            ))
            .catch((error) => console.error('Predefined file error:', error));
    };

    const sleep = async (milliseconds) => {
        return new Promise(resolve => setTimeout(resolve, milliseconds));
    };

// --- Helper: strictly drops the audio source to flush memory ---
const resetAudioElement = (audioElement) => {
    if (!audioElement) return;
    try { audioElement.pause(); } catch (_) {}
    try { audioElement.currentTime = 0; } catch (_) {}
    
    // Spec-compliant flush
    audioElement.removeAttribute('src');
    audioElement.load(); 
};

const getPlaybackTiming = (sentence) => ({
    timingMode: sentence?.timingMode,
    fixedDelaySeconds: sentence?.fixedDelaySeconds,
    autoplayPlaybackRate: sentence?.autoplayPlaybackRate,
});

const getEffectivePlaybackRate = (fallbackRate, playbackTiming, isAutoplayActive) => {
    if (isAutoplayActive) {
        const autoplayRate = Number(playbackTiming?.autoplayPlaybackRate);
        if (Number.isFinite(autoplayRate) && autoplayRate > 0) {
            return autoplayRate;
        }
    }
    return fallbackRate;
};

const getInterSentenceDelay = (duration, playbackTiming) => {
    if (playbackTiming?.timingMode === "fixedDelay") {
        const fixedDelay = Number(playbackTiming.fixedDelaySeconds);
        return Number.isFinite(fixedDelay) ? fixedDelay : 1;
    }
    return duration;
};

const playAudio1 = async ({
    audioData,
    duration = 2,
    playbackRate = 1.0,
    times = 1,
    resumeFrom = 0,
    resume = false,
    played = 0,
    playbackTiming = null,
}) => {
    setIsPlaying(true);
    setPaused(false);
    setShowPlaybackControls(true);

    let playCount = played || 0;

    // --- Reuse a single <audio> element to preserve Autoplay permissions ---
    if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.preload = "auto";
    }
    const audioElement = audioRef.current;

    // --- Configure audio source ---
    if (resume) {
        audioElement.playbackRate = playbackRate;
        audioElement.currentTime = resumeFrom;
    } else {
        // Drop the old buffer natively
        resetAudioElement(audioElement);

        audioElement.src = audioData;  // Blob URL
        audioElement.playbackRate = playbackRate;
        audioElement.currentTime = 0;
        
        // Note: We deliberately removed the manual 'canplay' EventListener. 
        // The browser's native .play() Promise automatically waits for buffering!
    }

    // --- Play Wrapper ---
    const playOnce = () =>
        new Promise((resolve, reject) => {
            audioElement.onended = resolve;
            
            // Fast-fail if the blob is corrupted or network fails
            audioElement.onerror = (e) => reject(e);

            const playPromise = audioElement.play();
            if (playPromise !== undefined) {
                playPromise.catch((err) => {
                    console.error("Audio play failed / Autoplay blocked:", err);
                    ctx.dispatch({ type: BasicEvents.STOP, error: true, timestamp: Date.now() });
                    reject(err);
                });
            }
        });

    try {
        // --- Repeat logic ---
        while (playCount < times && !isPausedRef.current) {
            await playOnce();
            playCount++;
            setRepeatPlayed(playCount);

            if (playCount < times && !isPausedRef.current) {
                await sleep(1000); // small delay between repeats
                audioElement.currentTime = 0;
            }
        }
    } catch (e) {
        console.error("Playback sequence error:", e);
    } finally {
        setIsPlaying(false);

        const finishedNaturally = !isPausedRef.current && playCount >= times;

        if (finishedNaturally) {
            resetAudioElement(audioElement); // Flush buffer upon completion

            setShowPlaybackControls(false);
            setPausedAt(0);
            setRepeatPlayed(0);
            setRepeatTotal(1);

            // --- FSM Autoplay continuation ---
            if (isAutoPlayActive) {
                const interSentenceDelay = getInterSentenceDelay(duration, playbackTiming);

                ctx.dispatch({
                    type: BasicEvents.PLAY_END,
                    timestamp: Date.now(),
                    playDuration: interSentenceDelay,
                });
            }
        }
    }
};




    const playAudio1_0 = async ({
        audioData,
        duration = 2,
        playbackRate = 1.0,
        times = 1,
        resumeFrom = 0,
        resume = false,
        played = 0,
        }) => {
            setIsPlaying(true);
            setPaused(false);
            setShowPlaybackControls(true);

            let playCount = played || 0;

            // --- Reuse a single <audio> element ---
            if (!audioRef.current) {
                audioRef.current = new Audio();
                audioRef.current.preload = "auto";
            }

            const audioElement = audioRef.current;

            if (resume) {
                audioElement.playbackRate = playbackRate;
                audioElement.currentTime = resumeFrom;
            } else {
                // reuse element, just swap the source
                // cleanup
                audioElement.pause();
                audioElement.currentTime = 0;

                // force unload
                audioElement.removeAttribute("src");
                audioElement.load();

                audioElement.src = `data:audio/mp3;base64,${audioData}`;
                audioElement.playbackRate = playbackRate;
                audioElement.currentTime = 0;
            }

            const play = () =>
                new Promise((resolve, reject) => {
                audioElement.onended = resolve;
                audioElement.onerror = reject;

                const playPromise = audioElement.play();
                if (playPromise !== undefined) {
                    playPromise.catch((err) => {
                    console.error("Audio play failed:", err);
                    // Let FSM know playback failed → stop autoplay
                    ctx.dispatch({
                        type: BasicEvents.STOP,
                        error: true,
                        timestamp: Date.now(),
                    });
                    reject(err);
                    });
                }
                });

            try {
                while (playCount < times && !isPausedRef.current) {
                await play();
                playCount++;
                setRepeatPlayed(playCount);

                if (playCount < times && !isPausedRef.current) {
                    await sleep(1000); // your existing helper
                    audioElement.currentTime = 0;
                }
                }
            } catch (e) {
                // optional: log or surface error
            } finally {
                setIsPlaying(false);
                if (!isPausedRef.current) {
                setShowPlaybackControls(false);
                setPausedAt(0);
                setRepeatPlayed(0);
                setRepeatTotal(1);

                // Only signal FSM if autoplay is active
                if (isAutoPlayActive) {
                    const interSentenceDelay = getInterSentenceDelay(
                        duration,
                        getPlaybackTiming(predefSentence)
                    );
                    ctx.dispatch({
                    type: BasicEvents.PLAY_END,
                    timestamp: Date.now(),
                    playDuration: interSentenceDelay,
                    });
                }
                }
            }
        };
    

    const handleGenderChoiceChange = async (e) => {
        let genderValue = e.target.value;
        setSelectedGender(genderValue);
        try {
            await updateConfig(genderKey, genderValue);
        } catch (err) {
            if (props.errorCallBack) { props.errorCallBack(err); } else {
                setError(err);
            }
        }
        setLastInputModified(true);
        setIsRepeatPlaying(false);
        handleStop ();
    }
    // When 3x is clicked
    const handleRepeatClick = () => {
        setPausedAt(0);
        setIsRepeatPlaying(true); // Show playback controls for 3x
        setRepeatTotal(3);
        setRepeatPlayed(0);
        // playAudio(audioData.audio, playbackRate, 3, 0, false, 0);
        playAudio1({
            audioData: audioData.audio,
            duration: audioData.duration,
            playbackRate: playbackRate,
            times: 3,
            resumeFrom: 0,
            resume: false,
            played: 0,
            playbackTiming: audioData.playbackTiming || getPlaybackTiming(predefSentence),
        });
    };

    const handleAddClick = () => {
        setAddInProgress(true);
        // tricking react into updating the button state
        const DELAY_ADD_CONTENT = 300;
        setTimeout(() => {
            setIsPopupOpen(true);
            // Will re-enable after popup closes (see handlePopupAdd/handlePopupCancel)
        }, DELAY_ADD_CONTENT); // Let React update the button state first

    };

    const addToMyContent = (insertMode = myContentInsertMode) => {
        try {
            if (practiceTextValue.trim()) {
                const myCobj = { text: practiceTextValue };
                myCobj['insertMode'] = insertMode;
                if (audioData) {
                    myCobj['audioData'] = audioData;
                }
                myCobj['explanations'] = [...explanations.current];
                props.addToContentCallBackVR(myCobj);

            } 
        } catch (error) {
            console.error("Error adding to MyContent:", error);
            if (props.errorCallBack) { 
                props.errorCallBack(error);                 
            } else {
                setError(error);
            }

        }
    }

    const handlePopupAdd = async (insertMode) => {
        try {
            // Wait for the content to be updated to "MyContent"
            // await updateConfig(configKeys.currentContent, "MyContent");
    
            // Add the sentence to "MyContent"
            // switch to "MyContent" and add the sentence
            if (!props.dualView) {
                if (props.toggleMode) {
                    props.toggleMode(); 
                }
            }
            setMyContentInsertMode(insertMode);
            addToMyContent(insertMode);
 
    
            // Close the popup
            setIsPopupOpen(false);
        } catch (error) {
            console.error("Error updating content to MyContent:", error);
            setAddInProgress(false); // Re-enable in case of fallure
        } 
    };

    const handlePopupCancel = () => {
        setIsPopupOpen(false);
        setAddInProgress(false); // Re-enable if user cancels
    };

    const explanationCallBack = (eObj) => {
        if (! explanations.current.some((expl) => expl.explanation === eObj.explanation)) {
            explanations.current.push(eObj)
        }
        // console.log('Synthesizer: ', explanations.current)
    }

    const showDuration = () => {
        if (!audioData || typeof audioData.duration == 'undefined') {
            return '';
        } else {
            // Adjusting duration for stop clicks (if necessary)
            // let stop_click_adjustment = 0.25; // Example adjustment
            return '';  // hiding duration as per request
            let duration = audioData.duration //+ stop_click_adjustment;
            return `${duration.toFixed(2)}s`;
        }
    };
    const isMycontent = () => { return config[configKeys.currentContent].includes("MyContent")};


    const disableMyContentButton = () => {
        let disable = false;
    disable = disable || (practiceTextValue === '');
    disable = disable || (predefSentence?.getSentence() === practiceTextValue);
        disable = disable || addInProgress || isPlaying;
        return disable;
    }

    // On pause, just store pausedAt and repeatPlayed (already handled)
    const handlePause = () => {
        if (audioRef.current) {
            setPaused(true);
            setIsPlaying(false);
            setPausedAt(audioRef.current.currentTime);
            audioRef.current.pause();
        }
    };

    const handleStop = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setPaused(false);
            setIsPlaying(false);
            setPausedAt(0);
            setShowPlaybackControls(false);
        }
    };

    // Resume logic: if paused, resume from repeatPlayed
    const handleResume = (times = repeatTotal) => {
        if (isPaused && audioRef.current && audioData && audioData.audio) {
        
            // playAudio(
            //     audioData.audio,
            //     playbackRate,
            //     repeatTotal,
            //     pausedAt,
            //     true,
            //     repeatPlayed
            // );
            playAudio1({
                audioData: audioData.audio,
                duration: audioData.duration,
                playbackRate: getEffectivePlaybackRate(
                    playbackRate,
                    audioData.playbackTiming || getPlaybackTiming(predefSentence),
                    isAutoPlayActive
                ),
                resumeFrom: pausedAt,
                times: times,
                resume: true,
                played: repeatPlayed,
                playbackTiming: audioData.playbackTiming || getPlaybackTiming(predefSentence),
            });
        }
    };

    const setPaused = (val) => {
        setIsPaused(val);
        isPausedRef.current = val;
    };

    const isShowingPlaybackControls = () => {
    let ret = (showPlaybackControls && (isRepeatPlaying || practiceTextValue.length > 40));
        // ret = ret && !isAutoPlayActive;
        return ret;
    };

    const playOrSynthsize = (voice) => {
        setSelectedGender(voice);
        if (isPaused ) {
             handleResume(1)
        }
        else { 
            handleClickSynthesize(voice); 
        }
    }
    

    const disableContainer = (autoPlayState) => ({
    // height: '80vh',
    // background: '#f5f5f5',
    // width: '100%',
    // maxWidth: '100vw',
    // overflowX: 'auto',
    opacity: (autoPlayState !== 'idle') ? 0.5 : 1,
    pointerEvents: (autoPlayState !== 'idle') ? 'none' : 'auto',
    transition: 'opacity 0.2s',
    });


    return (
        <Paper 
        elevation={3} 

        sx={{ 
            backgroundColor: theme.customColors.bgSynthesyzer,
            px: 1,
            pt: 1,
            pb: 1,
            borderRadius: 2,
            position: 'relative',
            width: '100%',
            ...disableContainer(ctx.autoPlayState)
        }}
    >
        <Box
            sx={{
                m: 1,
                ml: 1,
                display: 'flex',
                flexDirection: 'row',
                mb: 1,
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                width: '100%',
                
            }}
        >
            <Button
                title="Pops up translation form"
                onClick={openFormTranslate}
                startIcon={<TranslateIcon />}
                variant="contained"
                color="primary"
                disabled={(config[configKeys.langLearn].value === config[configKeys.langBase].value) }
                sx={{
                    textTransform: 'none',
                    fontSize: 16,
                    // padding: 1,
                    paddingY: 0.5,
                }}
            >
                TRANSLATE
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', mx: 2, gap: 1 }}>
                <Button
                    title="Add to MyContent"
                    onClick={handleAddClick}
                    variant="outlined"
                    disabled={disableMyContentButton()}
                    startIcon={<ArrowBack />}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 5,
                        whiteSpace: 'nowrap',
                        maxHeight: 36,
                        // alignSelf: 'flex-end',
                    }}
                >
                    MyContent
                </Button>
            </Box>
            <Tooltip title="Clear text">
            <IconButton
                onClick={() => {
                    setPracticeTextValue('');
                    resetState();
                    props.inputTextCallBack("");
                }}
                size="large"
                sx={{ mr: 2, 
                    // my: 0, 
                    mb :0,
                    width: 48, 
                    height: 48 
                }}
            >
                <Delete sx={{ fontSize: 32 }} />
            </IconButton>
            </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 1, ml: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, width: '99%' }}>
                <Box sx={{ flexGrow: 1, m: 0, display: 'flex', position: 'relative', width: '100%' }}>
                    <TextField
                        ref={textareaRef}
                        label={`Type text in ${lang} or use TRANSLATE`}
                        multiline
                        minRows={2}
                        value={practiceTextValue}
                        onChange={handleInputChange}
                        variant="outlined"
                        fullWidth
                        spellCheck="false"
                        autoComplete="off"
                        // apply maxlength to the underlying input/textarea and keep dir/style
                        slotProps={{
                                    input: {
                                    dir: isRTL(lang) ? 'rtl' : 'ltr',
                                    style: { textAlign: isRTL(lang) ? 'right' : 'left' },
                                    maxLength: 200
                                    }
                        }}
                        sx={{ backgroundColor: 'whitesmoke', ml: 0, flexGrow: 1, pb:3 }}
                    />
                    {/* Character counter shown inside the TextField (bottom-left) */}
                    <Box sx={{ position: 'absolute', left: 12, bottom: 2, pointerEvents: 'none' }}>
                        <Typography variant="caption" sx={{ color: 'gray' }}>
                            {`${practiceTextValue.length}/${CHARLIMIT}`}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: "column", mt: 1, width: "100%" }}>
                <Box sx={{ mb: 0 }}>
                    <FormInfo
                        inputText={practiceTextValue}
                        langLearn={config[configKeys.langLearn].label}
                        langBase={config[configKeys.langBase].label}
                        explanationCallBack={explanationCallBack}
                        sentence={props.inputSentence}
                        sentenceCallBack={props.sentenceCallBack}
                        selectedGender={selectedGender}
                        handleGenderChoiceChange={handleGenderChoiceChange}
                        errorCallBack={(err) => props.errorCallBack(err)}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    {addInProgress &&
                        <Box display="flex" justifyContent="center" alignItems="center">
                            <CircularProgress />
                        </Box>
                    }
                    { !isAutoPlayActive &&
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', width: '100%', minHeight: 48 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-center', mx: 'auto', gap: 3 }}>
                            {isShowingPlaybackControls() ? (
                                <>
                                    <IconButton
                                        onClick={isPaused ? () => handleResume(isRepeatPlaying ? repeatTotal : 1) : handlePause}
                                        disabled={!isPlaying && !isPaused }
                                        color="primary"
                                        sx={{ width: 36, height: 36 }}
                                    >
                                        {isPaused ? <PlayArrow sx={{ fontSize: 28 }} /> : <Pause sx={{ fontSize: 28 }} />}
                                    </IconButton>
                                    <IconButton
                                        onClick={handleStop}
                                        disabled={!isPlaying && !isPaused }
                                        color="primary"
                                        sx={{ ml: 2, width: 36, height: 36 }}
                                    >
                                        <Stop sx={{ fontSize: 28 }} />
                                    </IconButton>
                                </>
                            ) : (
                                <>
                                    <Button
                                        onClick={isPaused ? () => handleResume(3) : handleRepeatClick}
                                        disabled={!audioData || isPlaying }
                                        startIcon={<Loop />}
                                        variant="outlined"
                                        color="primary"
                                        sx={{
                                            mr: 2,
                                            textTransform: 'none',
                                            whiteSpace: 'nowrap',
                                            borderRadius: 5,
                                            alignSelf: 'flex-end',
                                        }}
                                    >
                                        3x
                                    </Button>
                                    <VoiceSelector
                                        onPlayMale={() => { playOrSynthsize("MALE") }}
                                        onPlayFemale={() => { playOrSynthsize("FEMALE") }}
                                        disabled={!practiceTextValue || isPlaying }
                                        disabledGender={disabledGender}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                    }
                </Box>
                        
                <Box sx={{ mt: 2, width: '100%' }}>
                    {showDuration() &&
                        <Box display="flex" alignItems="center" sx={{ ml: 1 }}>
                            {/* <AccessTime sx={{ color: 'blue', fontSize: 24, mr: 1 }} /> */}
                            <Timer sx={{ color: 'blue', fontSize: 24, mr: 1 }} />
                            <Typography
                                variant="body1"
                                title="Duration of the native (AI) speech"
                                sx={{ fontSize: '14px' }}
                            >
                                {` ${showDuration()}`}
                            </Typography>
                        </Box>
                    }
                    { !isAutoPlayActive &&
                    <PlaybackSlider
                        playbackRate={playbackRate}
                        handlePlaybackRateChange={(e) => {
                            setPlaybackRate(parseFloat(e.target.value));
                            playOnRateChange(parseFloat(e.target.value));
                        }}
                        resetPlaybackRate={(pr) => {
                            setPlaybackRate(pr);
                            playOnRateChange(pr);
                        }}
                        limits={[0.5, 1.0]}
                        step={0.1}
                        title="Adjust playback speed"
                        disabled={isPlaying }
                    />
                    }
                </Box>
            </Box>
            <FormTranslate
                isModalOpen={isFormTranslateOpen}
                onClose={closeFormTranslate}
                inputText={props.inputText}
                inputTextCallBack={props.inputTextCallBack}
                errorCallBack={(err) => props.errorCallBack(err)}
            />

            {error && <ErrorDispatcher
                error={error}
                navigateToLogin={true}
                showDialog={true}>
            </ErrorDispatcher>}
            <AddToMyContentDialog
                open={isPopupOpen}
                sentenceText={practiceTextValue}
                currentContent={config[configKeys.currentContent]}
                isMyContent={isMycontent()}
                isMyContentEmpty={props.isCurrentMyContentEmpty}
                onCancel={handlePopupCancel}
                onAdd={handlePopupAdd}
            />
        </Box>
    </Paper>
    );
};

export { SpeechSynthesizer, synthesizeAudio };
